import React, { useEffect, useState } from "react";
import { Button, Row } from "react-bootstrap";
import Axios from "axios";
import moment from "moment-timezone";
import ReactEcharts from "echarts-for-react";

import fonts_config from "./fonts_config.json";
import pdfFonts from "pdfmake/build/vfs_fonts.js";
import pdfMake from "pdfmake/build/pdfmake.js";

import { useFormatter } from "./../../../util/formatter";
import { useSolarCalc } from "./../../../util/calculations";
import GYAxios from "../../../util/GYAxios";
import usePeakLabels from "../../../util/peakLabels";
import { sortByDate } from "../../../util/sorting";
import rfdc from 'rfdc';
import LoaderGraph from "../../general/LoaderGraph";

const clone = rfdc();
pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfMake.vfs;
pdfMake.fonts = fonts_config;

function TechnicalReportTemplate({ onDocDefinition = () => { }, ...props }) {
	const siteValue = props?.selectedSite.value;
	const hideUnusedTable = siteValue.showUnused === true && siteValue.additional_info?.report;
	const solarCalc = useSolarCalc();
	const [formatNumber, isEmpty] = useFormatter();
	const peakLabels = usePeakLabels(props.selectedSite.value.country);

	const [PDF, setPDF] = useState(null);
	const [PDFDoc, setPDFDoc] = useState(null);
	const [site, setSite] = useState(null);
	const [dailyData, setDailyData] = useState(null);
	const [monthlyData, setMonthlyData] = useState(null);
	const [yearlyData, setYearlyData] = useState([]);
	const [index, setIndex] = useState(null);
	const [invoiceName, setInvoiceName] = useState(null);
	const [optionsProd, setOptionsProd] = useState(null);
	const [optionsPR, setOptionsPR] = useState(null);
	const [graphProdRef, setGraphProdRef] = useState(null);
	const [graphPRRef, setGraphPRRef] = useState(null);
	const [issues, setIssues] = useState([]);

	const publicHolidayColour = "#88b917";
	const weekendColour = "#fcdd72";

	const dateStr = siteValue.dateStr;
	const fullMonthYear = moment(dateStr, "MMM-YYYY").format("MMMM YYYY");
	const startLastMonth = moment(dateStr, "MMM-YYYY").startOf("month");
	const endLastMonth = moment(dateStr, "MMM-YYYY").endOf("month");
	const dateOfReport = moment(siteValue.date, "DD/MM/YYYY").format("DD/MM/YYYY");

	const cod = moment(siteValue.cod, "YYYY-MM-DD").add(1, 'day');
	const selectedM = moment(siteValue.date, "DD/MM/YYYY");
	const contractY = moment(selectedM).diff(cod, 'years') + 1;
	const codM = moment(cod, "DD/MM/YYYY").format("MM-YYYY");
	const starting = moment(codM, "MM-YYYY").add(contractY - 1, "y");
	const startingM = moment(starting, "MM-YYYY").format("MM-YYYY");
	const endingM = moment(selectedM, "MM-YYYY").format("MM-YYYY");

	const echartsStyle = {
		visibility: "hidden",
		position: "absolute",
		height: "340px",
		padding: "10px 0px 10px 0px",
		width: "1000px",
		margin: "auto",
	};

	/**
	 * Check if selected date is after the date we start showing yearly unused table
	 */
	const afterYearlyStart = (date) => {
		date = moment(date, 'DD-MM-YYYY').format("YYYY-MM-DD")
		return moment(date).isSameOrAfter('2023-05-01');
	}

	/**
	 * Download technical report generated
	 */
	const downloadPDF = () =>
		pdfMake.createPdf(PDFDoc).download(`${invoiceName}.pdf`);

	/**
	 * Format date string - (eg. 21-12-2021 - Tues)
	 * @param {Date string to be formatted} dateStr
	 */
	const formatDateData = (dateStr) =>
		moment(dateStr, "DD-MM-YYYY").format("DD-MM-YYYY - ddd");

	const formatMonthData = (str) => {
		const dateData = moment(str, 'YYYY-MM-DD').add(1, "days");
		return moment(dateData, 'YYYY-MM-DD').format('MM-YYYY');
	}

	/**
	 * Filter daily data according to COD and before today
	 */
	const filterDailyData = (tempDailyData, cod, correctMonthlyData, operation_status) => {
		const today = moment().format("YYYY-MM-DD");
		let arr = []
		if (!!correctMonthlyData && operation_status === 2) {
			arr = tempDailyData.filter((daily) => {
				const isBeforeCOD = moment(daily.dateData).isBefore(correctMonthlyData.dateData, "day");
				const isBeforeToday = moment(daily.dateData).isBefore(today, "day");

				return isBeforeCOD && isBeforeToday;
			});
		} else {
			arr = tempDailyData.filter((daily) => {
				const isAfterOrOnCOD = moment(daily.dateData).isSameOrAfter(cod, "day");
				const isBeforeToday = moment(daily.dateData).isBefore(today, "day");

				return isAfterOrOnCOD && isBeforeToday;
			});
		}
		return arr;
	};

	/**
	 * Fetch issues of selected site and month
	 * @param {obj} selectedSite
	 */
	const fetchIssues = (selectedSite) => {
		const siteData = selectedSite.value;
		const siteID = siteData.siteID;
		const selectedDate = siteData.date;
		const dateObj = moment(selectedDate, "DD/MM/YYYY");
		const dateString = dateObj.format("YYYY-MM-DD");

		// Fetch all issues of selected site
		const filterIssuesURI = `/issue/filter/search?column=issue.site_id&value=${siteID}`;
		const filterIssuesReq = GYAxios.get(filterIssuesURI);

		filterIssuesReq.then((res) => {
			const fetchedIssues = res.data.error === undefined ? res.data : [];
			const formattedIssues = fetchedIssues.map((data) => ({
				...data,
				dateData: data.issue_date,
			}));

			// Filter issues of selected month or unresolved issues of previous months
			// Unresolved issues of later months are removed from the array
			const selectedDateObj = moment(selectedDate, "DD/MM/YYYY");

			const selectedIssues = formattedIssues.filter((issue) => {
				// Valid start and end date
				const issueStartDate = moment(issue.issue_date).format("YYYY-MM-DD");
				const issueEndDate = moment(issue.resolved_date).format("YYYY-MM-DD");

				// The issue date is between issue date and resolved date
				const isIssueOfSelectedMonth = selectedDateObj.isBetween(
					issueStartDate,
					issueEndDate,
					"month",
					"[]"
				);

				// The issue is unresolved
				const isUnresolved = issue.status === 0;

				return isIssueOfSelectedMonth || isUnresolved;
			});

			const sortedIssues = selectedIssues.sort((a, b) =>
				sortByDate(a, b, "ASC")
			);

			fetchActions(sortedIssues, dateString);
		});
	};

	/**
	 * Fetch actions of fetched issues
	 * @param {array} selectedIssues
	 * @param {string} dateString
	 */
	const fetchActions = (selectedIssues, dateString) => {
		// Get array of req URIs
		const actionsURIs = selectedIssues.map(
			(issue) => `/actions/issue/${issue.issue_id}`
		);

		// Get array of API requests
		const actionsReqs = actionsURIs.map((URI) => GYAxios.get(URI));

		Axios.all(actionsReqs).then(
			Axios.spread((...responses) => {
				// Handle error
				const actionsRes = responses.map((res) =>
					res.data.error === undefined ? res.data : []
				);

				// Filter actions that are of selected month and before and client desc not null
				const filteredActions = actionsRes.map((actionArray) => {
					// Remove actions that are later than selected month
					const filteredArray = actionArray.filter((action) => {
						const isIssueOfSelectedMonth =
							moment(action.action_date).isSameOrBefore(dateString, "month") &&
							action.cl_action != null;
						return isIssueOfSelectedMonth;
					});

					// Prepare actions for sorting function
					const formattedActions = filteredArray.map((actions) => ({
						...actions,
						dateData: actions.action_date,
					}));

					const sortedActions = formattedActions.sort((a, b) =>
						sortByDate(a, b, "ASC")
					);
					return sortedActions;
				});

				// Map issues to the respective actions
				const mappedIssues = selectedIssues.map((issue, i) => {
					const newIssue = {
						...issue,
						actions: filteredActions[i],
					};

					return newIssue;
				});

				setIssues(mappedIssues);
			})
		);
	};

	/**
		 * Fetch unused of selected site and months
		 * @param {obj} selectedSite
		 */
	const fetchUnused = (selectedSite) => {
		const uri = `/reports/deo/`;
		const siteID = selectedSite.value.siteID;
		const body = { siteID, startingM, endingM }
		Axios.post(uri, body).then((res) => {
			const data = res.data.error === undefined ? res.data : [];
			setYearlyData(data);
		})
	}

	/**
	 *
	 * @param {Daily data retrieved} data
	 */
	const calculateData = (dailyData, monthlyData, operation_status) => {
		let tempDailyData = dailyData;

		let correctMonthlyData = monthlyData[0];
		const cod = moment(correctMonthlyData.dateData).format("YYYY-MM-DD");
		if (monthlyData.length === 2) {
			tempDailyData = filterDailyData(tempDailyData, cod, correctMonthlyData, operation_status);
			if (operation_status === 2) {
				correctMonthlyData = monthlyData[1];
			}
		} else {
			tempDailyData = filterDailyData(tempDailyData, cod, operation_status);
		}
		const formattedData = tempDailyData.map((row) => {
			const irradiation = solarCalc.calculateIrr(row.irradiance_sum);
			return {
				dateData: moment(row.dateData).format("DD-MM-YYYY"),
				solarON: row.solar_ON,
				solarOFF: row.solar_OFF,
				solarTotal: row.solar_total,
				PR: row.brut_PR,
				irradiation,
			};
		});
		setMonthlyData(correctMonthlyData);
		setDailyData(formattedData);
	};

	/**
	 * Format Actions line in Issues table
	 * @param {*} actions
	 * @returns actions lines
	 */
	const formatActionsStr = (actions) => {
		let actionStr = "";

		if (actions != null && actions.length > 0) {
			// Get strings of formatted actions per issue
			const formattedActions = actions.map((action, i) => {
				const isValidDate =
					moment(action.action_date).format("DD/MM/YYYY hh:mmA") !==
					"Invalid date";

				const actionDate = isValidDate
					? moment(action.action_date).format("DD/MM/YYYY hh:mmA")
					: "";

				const actionDesc = action.cl_action ? ` : ${action.cl_action}` : "";
				const actionFinal = `${actionDate} ${actionDesc}`;
				return actionFinal;
			});
			// Join the formatted actions with line break
			actionStr = formattedActions.join("\n");
		}

		return actionStr;
	};

	/**
	 *
	 * @param {Date string} dateStr
	 * @param {Dates of public holidays} holidayDates
	 */
	const getOffPeakRows = (dateStr, holidayDates, country) => {
		const date = moment(dateStr, "DD-MM-YYYY");
		const day = date.format("ddd");

		switch (country) {
			case "TH":
				// Public Holiday
				if (holidayDates.includes(dateStr))
					return ["mainTable", "pubHolidayRow"];
				// Weekend
				else if (day === "Sun" || day === "Sat")
					return ["mainTable", "weekendRow"];
				// Regular dates
				else return ["mainTable"];
			case "VN":
				if (day === "Sun") return ["mainTable", "weekendRow"];
				else return ["mainTable"];
			default:
				break;
		}
	};

	/**
	 * Format GRP meter cells based on number of index of non-EDMI meters
	 */
	const formatGRPMeterRow = (startIndex, endIndex, diff, size, i) => {
		let cells = [];

		if (size > 1) {
			let headerTitle = "Meter GRP ";
			headerTitle += parseInt(i + 1);
			cells = [
				{
					text: `${headerTitle}`,
					border: [true, true, false, true],
				},
				{
					text: `${formatNumber(startIndex, 3)}`,
					border: [false, true, false, true],
				},
				{
					text: `${headerTitle}`,
					border: [true, true, false, true],
				},
				{
					text: `${formatNumber(endIndex, 3)}`,
					border: [false, true, false, true],
				},
				{
					text: `${headerTitle}`,
					border: [true, true, false, true],
				},
				{
					text: `${formatNumber(diff, 3)}`,
					border: [false, true, false, true],
				},
				{
					text: formatNumber(monthlyData.solar_total, 3),
					style: ["boldHeader", { alignment: "center" }],
					colSpan: 2,
					rowSpan: size,
					border: [true, true, true, true],
				},
				"",
			];
		} else {
			cells = [
				{
					colSpan: 2,
					text: `${formatNumber(startIndex, 3)}`,
					border: [true, false, true, true],
					style: [{ alignment: "center" }],
				},
				"",
				{
					colSpan: 2,
					text: `${formatNumber(endIndex, 3)}`,
					border: [false, false, true, true],
					style: [{ alignment: "center" }],
				},
				"",
				{
					colSpan: 2,
					text: `${formatNumber(diff, 3)}`,
					border: [false, false, true, true],
					style: [{ alignment: "center" }],
				},
				"",
				{
					text: formatNumber(monthlyData.solar_total, 3),
					style: ["boldHeader", { alignment: "center" }],
					colSpan: 2,
					rowSpan: size,
					border: [false, true, true, true],
				},
				"",
			];
		}
		return cells;
	};

	/**
	 * Format GRP meter cells based on number of index of EDMI meters
	 * @param {string} startIndex
	 * @param {string} endIndex
	 * @param {string} totalDiff
	 * @param {string} labelText
	 * @returns GRP meter cells table opts
	 */
	const formatEDMIMeterRow = (
		startIndex,
		endIndex,
		totalDiff,
		labelText,
		i
	) => {
		const diff = formatNumber(endIndex - startIndex, 3);

		const cells = [
			{
				text: `${labelText}`,
				border: [true, true, false, true],
				style: isOddIndex(i) ? ["oddCells"] : "",
			},
			{
				text: `${formatNumber(startIndex, 3)}`,
				border: [false, true, false, true],
				style: isOddIndex(i) ? ["oddCells"] : "",
			},
			{
				text: `${labelText}`,
				border: [true, true, false, true],
				style: isOddIndex(i) ? ["oddCells"] : "",
			},
			{
				text: `${formatNumber(endIndex, 3)}`,
				border: [false, true, false, true],
				style: isOddIndex(i) ? ["oddCells"] : "",
			},
			{
				text: `${labelText}`,
				border: [true, true, false, true],
				style: isOddIndex(i) ? ["oddCells"] : "",
			},
			{
				text: diff,
				border: [false, true, false, true],
				style: isOddIndex(i) ? ["oddCells"] : "",
			},
			{
				text: formatNumber(totalDiff, 3),
				style: ["boldHeader", { alignment: "center" }],
				colSpan: 2,
				rowSpan: 2,
				border: [true, true, true, true],
			},
			"",
		];

		return cells;
	};

	/**
	 * Get table opts of EDMI meters index
	 * @param {obj} monthlyData
	 * @returns table opts for EDMI index
	 */
	const formatEDMITableOpts = (monthlyData) => {
		const startIndexONSplit = monthlyData.ON_start_index.split(",");
		const endIndexONSplit = monthlyData.ON_end_index.split(",");
		const startIndexOFFSplit = monthlyData.OFF_start_index.split(",");
		const endIndexOFFSplit = monthlyData.OFF_end_index.split(",");

		const startIndexONArray = startIndexONSplit.map((str) => str.trim());
		const endIndexONArray = endIndexONSplit.map((str) => str.trim());
		const startIndexOFFArray = startIndexOFFSplit.map((str) => str.trim());
		const endIndexOFFArray = endIndexOFFSplit.map((str) => str.trim());

		const size = startIndexONArray.length;
		let meterArray = [];

		for (let i = 0; i < size; i++) {
			const meterName = size > 1 ? ` meter ${i + 1}` : "";
			const ONDiff = endIndexONArray[i] - startIndexONArray[i];
			const OFFDiff = endIndexOFFArray[i] - startIndexOFFArray[i];
			const totalDiff = ONDiff + OFFDiff;

			const obj = {
				meterName,
				peakLabels,
				startIndexes: [startIndexONArray[i], startIndexOFFArray[i]],
				ONDiff,
				endIndexes: [endIndexONArray[i], endIndexOFFArray[i]],
				OFFDiff,
				totalDiff,
			};

			meterArray.push(obj);
		}

		let tempOpts = [];
		meterArray.forEach((meter) => {
			peakLabels.forEach((label, i) => {
				const labelText = `${label} ${meter.meterName}`;
				const totalDiff = meter.totalDiff;
				const startIndex = meter.startIndexes[i];
				const endIndex = meter.endIndexes[i];

				tempOpts.push(
					formatEDMIMeterRow(startIndex, endIndex, totalDiff, labelText, i)
				);
			});
		});
		return tempOpts;
	};

	/**
	 * Get GRP meter reading of site
	 * @param {Monthly data} monthlyData
	 */
	const getGRPMeterTable = (site, monthlyData, dailyData) => {
		const isEDMI = site.value.isEDMI;
		const lastID = dailyData.length - 1;

		// Date format
		const initFormat = "DD-MM-YYYY";
		// Display date format
		const displayFormat = "DD-MMM-YYYY";
		const firstDateObj = dailyData[0].dateData;
		const lastDateObj = dailyData[lastID].dateData;

		const firstDate = moment(firstDateObj, initFormat).format(displayFormat);
		const lastDate = moment(lastDateObj, initFormat).format(displayFormat);

		let tableOpt = [
			[
				{
					text: `Index Meter of ${firstDate}`,
					style: "tableHeader",
					colSpan: 2,
					border: [true, true, false, true],
				},
				"",
				{
					text: `Index Meter of ${lastDate}`,
					style: "tableHeader",
					colSpan: 2,
					border: [true, true, false, true],
				},
				"",
				{
					text: "Number of kWh produced",
					style: "tableHeader",
					colSpan: 2,
					border: [true, true, false, true],
				},
				"",
				{
					text: "Total",
					style: ["tableHeader", "boldHeader"],
					colSpan: 2,
					border: [true, true, true, false],
				},
				"",
			],
		];

		// Build GRP indexes rows with ON and OFF peak separation per injection point
		if (isEDMI) tableOpt = [...tableOpt, ...formatEDMITableOpts(monthlyData)];
		//
		else {
			// Build GRP indexes per injection point
			if (
				monthlyData.GRP_start_index != null &&
				monthlyData.GRP_end_index != null
			) {
				const meterGRPStartArr = monthlyData.GRP_start_index.split(",");
				const meterGRPEndArr = monthlyData.GRP_end_index.split(",");
				const size = meterGRPStartArr.length;

				for (let i = 0; i < size; i++) {
					const startIndex = parseFloat(meterGRPStartArr[i]);
					const endIndex = parseFloat(meterGRPEndArr[i]);
					const diff = endIndex - startIndex;

					tableOpt.push(formatGRPMeterRow(startIndex, endIndex, diff, size, i));
				}
			}
			// Backup until automated update of monthly_calc for index is ready
			else {
				tableOpt.push([
					{
						colSpan: 2,
						text: `${index.firstDay}`,
						border: [true, true, false, true],
						alignment: "center",
					},
					"",
					{
						colSpan: 2,
						text: `${index.lastDay}`,
						border: [true, true, false, true],
						alignment: "center",
					},
					"",
					{
						colSpan: 2,
						text: `${index.diff}`,
						border: [true, true, false, true],
						alignment: "center",
					},
					"",
					{
						text: formatNumber(monthlyData.solar_total, 3),
						style: ["boldHeader", { alignment: "center" }],
						colSpan: 2,
						rowSpan: 1,
						border: [true, true, true, true],
					},
					{ text: "" },
				]);
			}
		}

		return tableOpt;
	};

	/**
	 * Construct body of issues table
	 * @param {Issues of selected site} issues
	 */
	const getIssuesTable = (issues, operation_status, monthlyData, cod) => {
		if (moment(monthlyData.dateData).diff(cod, 'months') === 0) {
			if (operation_status === 2) {
				issues = issues.filter((issue) => {
					const thisMonth = moment(issue.issue_date).isSameOrAfter(moment(monthlyData.dateData).startOf('month'), "day");
					const beforeCOD = moment(issue.issue_date).isBefore(moment(cod), "day");
					return thisMonth && beforeCOD;
				})
			} else {
				issues = issues.filter((issue) => {
					return moment(issue.issue_date).isSameOrAfter(moment(monthlyData.dateData), "day");
				})
			}
		}
		let tableOpt = [
			[
				{
					text: "REMARKS",
					style: "tableHeader",
					border: [true, true, false, true],
				},
				{
					text: "CORRECTIVE ACTIONS",
					style: "tableHeader",
					border: [true, true, true, true],
				},
			],
		];
		if (issues != null && issues.length > 0) {
			issues.forEach((issue, i) => {
				const issueDate =
					moment(issue.issue_date).format("DD/MM/YYYY hh:mmA") !== "Invalid date"
						? moment(issue.issue_date).format("DD/MM/YYYY hh:mmA")
						: "";

				const remarkStr = issue.cl_issue != null ? ` : ${issue.cl_issue}` : "";
				const actionStr = formatActionsStr(issue.actions);

				// Border
				let leftColBorders = [true, false, false, true];
				let rightColBorders = [true, false, true, true];

				tableOpt.push([
					{
						text: `${issueDate} ${remarkStr}`,
						border: leftColBorders,
						style: isOddIndex(i) ? "oddCells" : "",
					},
					{
						text: actionStr,
						border: rightColBorders,
						style: isOddIndex(i) ? "oddCells" : "",
					},
				]);
			});
		} else {
			tableOpt.push([
				{ text: "-\n\n", border: [true, false, false, true] },
				{ text: "-\n\n", border: [true, false, true, true] },
			]);
		}

		return tableOpt;
	};

	/**
	 * Construct body of unused table
	 * @param {*} solarProduction
	 * @param {*} deemedEnergyOutput
	 * @param {*} unused 
	 * @param {*} consumption
	 */
	const getYearlyTable = (yearlyData) => {
		let sumProd = 0;
		let sumDEO = 0;
		let sumUnused = 0;
		// Header
		let table = [
			[
				{ text: "Month", style: "tableHeader" },
				{ text: "Solar Production (kWh)", style: "tableHeader" },
				{ text: "Deemed Energy Output (kWh)", style: "tableHeader" },
				{ text: "Unused (kWh)", style: "tableHeader" },
				{ text: "Consumption (%)", style: "tableHeader" },
			],
		];
		yearlyData.forEach((item) => {
			const calculatedDEO = solarCalc.DEOCalc(item.theoretical_PR, item.power, solarCalc.calculateIrr(item.irradiance_sum), item.m_GA_irr || 0, item.m_GA_prod || 0, item.m_TPA_irr || 0, item.m_TPA_prod || 0);
			const DEO = calculatedDEO > item.solar_total ? calculatedDEO : item.solar_total;
			const consumption = solarCalc.consumptionCalc(item.solar_total, DEO);
			const unused = DEO - item.solar_total
			sumProd += item.solar_total;
			sumDEO += DEO;
			sumUnused += unused;
			const row = [
				{
					text: formatMonthData(item.dateData),
					style: ["mainTable"]
				},
				{
					text: formatNumber(item.solar_total, 3),
					style: ["mainTable"]
				},
				{
					text: formatNumber(DEO, 3),
					style: ["mainTable"]
				},
				{
					text: formatNumber(unused > 0 ? unused : 0, 3),
					style: ["mainTable"]
				},
				{
					text: formatNumber(consumption, 2),
					style: ["mainTable"]
				},
			]
			table.push(row);
		})
		// Footer
		table.push([
			{ text: "TOTAL", style: "tableHeader" },
			{ text: formatNumber(sumProd, 3), style: "tableHeader" },
			{ text: formatNumber(sumDEO, 3), style: "tableHeader" },
			{ text: formatNumber(sumUnused, 3), style: "tableHeader" },
			{ text: formatNumber(solarCalc.consumptionCalc(sumProd, sumDEO), 2), style: "tableHeader" },
		]);
		return table;
	}

	/**
	 * Construct body of main table
	 * @param {*} dailyData
	 * @param {*} monthlyData
	 * @param {*} holidaysDates
	 */
	const getTableBody = (dailyData, monthlyData, holidaysDates) => {
		let tableOpt = [
			[
				{ text: "Date", style: "mainTable" },
				{ text: "Total Production (kWh)", style: "mainTable" },
				{ text: `${peakLabels[0]} Production (kWh)`, style: "mainTable" },
				{ text: `${peakLabels[1]} Production (kWh)`, style: "mainTable" },
				{ text: "Irradiation (kWh/m2)", style: "mainTable" },
				{ text: "PR (%)", style: "mainTable" },
			],
		];
		dailyData.forEach((row) => {
			const tempRow = [
				{
					text: formatDateData(row.dateData),
					style: getOffPeakRows(
						row.dateData,
						holidaysDates,
						site.value.country
					),
				},
				{
					text: formatNumber(row.solarTotal, 3),
					style: getOffPeakRows(
						row.dateData,
						holidaysDates,
						site.value.country
					),
				},
				{
					text: formatNumber(row.solarON, 3),
					style: getOffPeakRows(
						row.dateData,
						holidaysDates,
						site.value.country
					),
				},
				{
					text: formatNumber(row.solarOFF, 3),
					style: getOffPeakRows(
						row.dateData,
						holidaysDates,
						site.value.country
					),
				},
				{
					text: formatNumber(row.irradiation, 2),
					style: getOffPeakRows(
						row.dateData,
						holidaysDates,
						site.value.country
					),
				},
				{
					text: formatNumber(row.PR, 2),
					style: getOffPeakRows(
						row.dateData,
						holidaysDates,
						site.value.country
					),
				},
			];
			tableOpt.push(tempRow);
		});

		// Footer of Table
		tableOpt.push([
			{ text: "\n\nTOTAL", rowSpan: 2, style: ["mainTable", "boldHeader"] },
			{ text: "Total Production (kWh)", style: "mainTable" },
			{ text: `${peakLabels[0]} Production (kWh)`, style: "mainTable" },
			{ text: `${peakLabels[1]} Production (kWh)`, style: "mainTable" },
			{ text: "Irradiation (kWh/m2)", style: "mainTable" },
			{ text: "Actual PR (%)", style: "mainTable" },
		]);
		tableOpt.push([
			"",
			{
				text: formatNumber(monthlyData.solar_total, 3),
				style: ["mainTable", "boldHeader"],
			},
			{
				text: formatNumber(monthlyData.solar_ON, 3),
				style: ["mainTable", "boldHeader"],
			},
			{
				text: formatNumber(monthlyData.solar_OFF, 3),
				style: ["mainTable", "boldHeader"],
			},
			{
				text: formatNumber(monthlyData.irradiance_sum/6000, 2),
				style: ["mainTable", "boldHeader"],
			},
			{
				text: formatNumber(monthlyData.brut_PR, 2),
				style: ["mainTable", "boldHeader"],
			},
		]);

		return tableOpt;
	};

	/**
	 * Construct unused table based on selected site
	 * @param {Selected site} site
	 * @param {Monthly data} monthlyData
	 */
	const buildUnusedTable = (site, monthlyData) => {
		const theoreticalPR = monthlyData.theoretical_PR;
		const brutPR = monthlyData.brut_PR;
		const capacity = site.value.power;
		const irradiation = solarCalc.calculateIrr(monthlyData.irradiance_sum);
		const unusedValue = solarCalc.calculateUnused(
			theoreticalPR,
			brutPR,
			capacity,
			irradiation
		);

		const unusedThisMonth =
			unusedValue > 0 ? formatNumber(unusedValue, 3) : "0.00";

		let tableOpt = site.value.showUnused
			? {
				nodeName: "DIV",
				margin: [0, 15, 0, 0],
				table: {
					body: [
						[
							{
								text: "Monthly unused estimation (kWh)",
								style: "tableHeader",
							},
						],
						[unusedThisMonth],
					],
					widths: ["50%", "50%"],
				},
				layout: {},
			}
			: {};
		return tableOpt;
	};

	/**
	 * Set PDF name to be downloaded
	 * @param {Selected site} site
	 */
	const getInvoiceName = (site) => {
		let invName = site.invNo;
		let temp = site.customerID;
		const company = site.company;

		switch (company) {
			case "CPRAM":
				temp = `${temp.replace(" ", "_")}`;
				break;
			case "CP_ALL":
				temp = `${temp.split(" ").reverse().join("_")}`;
				break;
			case "Woodwork":
				temp = `${temp.replace(" ", "")}`;
				break;
			case "Southeast Textile":
				temp = `${temp.replace(" phase ", "")}`;
				break;
			case "SangRoong Group":
				temp = `${temp}`;
				break;
			case "Makro":
				const siteName = site.siteName;
				temp = `${company} ${siteName}`;
				break;
			case "UPI":
			case "FPI":
				temp = `${company}`;
				break;
			default:
				break;
		}

		invName = `${temp}-Report_${startLastMonth.format("MMM-YYYY")}`;
		setInvoiceName(invName);
	};

	/**
	 * Get number of solar panels and model
	 * @param {Selected site's data} site
	 */
	const getSolarPanels = (site) => {
		if (site.noSolarModules > 0 && site.solarModuleModel != null) {
			const noOfPanels = formatNumber(site.noSolarModules);
			const modelName = site.solarModuleModel.split("_").join(" ");
			return `${noOfPanels} Modules ${modelName} Wp`;
		} else return "-";
	};

	/**
	 * Make API request for daily data
	 * @param {Selected site} selectedSite
	 */
	const fetchDailyData = (selectedSite) => {
		setSite(selectedSite);

		// Parameters for API requests
		const period = {
			fromDate: startLastMonth.format("YYYY-MM-DD"),
			toDate: endLastMonth.format("YYYY-MM-DD"),
		};

		const lastMonthIndexParam = {
			table_name: selectedSite.table_name,
			duration: period,
			isEDMI: selectedSite.value.isEDMI,
		};

		const month = startLastMonth.format("YYYYMM");
		const siteID = selectedSite.value.siteID;

		const dateFormat = "YYYYMMDD";
		const fromDate = startLastMonth.format(dateFormat);
		const toDate = endLastMonth.format(dateFormat);
		const operation_status = selectedSite.value.operation_status
		// API request URI
		const dailyDataURI = `/daily/site/${siteID}/period/${fromDate}/${toDate}/order/dateData/ASC`;
		const monthlyReqURI = `/reports/technical/site/${siteID}/month/${month}`;

		// API Requests
		const energyDataDailyReq = GYAxios.get(dailyDataURI);
		const energyDataMonthlyReq = GYAxios.get(monthlyReqURI);

		const indexLastMonth = GYAxios.post(
			"/site/get_index_prev_month",
			lastMonthIndexParam
		);

		// Making API requests
		Axios.all([energyDataDailyReq, energyDataMonthlyReq, indexLastMonth]).then(
			Axios.spread((...responses) => {
				const dailyDataRes = responses[0];
				const monthlyDataRes = responses[1];
				const index = responses[2];

				dailyDataRes.data != null &&
					monthlyDataRes.data != null &&
					calculateData(dailyDataRes.data, monthlyDataRes.data, operation_status);

				let indexObj = {
					firstDay: 0,
					lastDay: 0,
					diff: 0,
				};

				if (index.data != null && index.data.length === 2) {
					const firstDay = formatNumber(index.data[0].prod, 3);
					const lastDay = formatNumber(index.data[1].prod, 3);
					const diff = formatNumber(index.data[1].prod - index.data[0].prod, 3);

					indexObj = {
						firstDay,
						lastDay,
						diff,
					};
				}

				setIndex(indexObj);
			})
		);
	};

	/**
	 * Generate graphs of report
	 * @param {Daily data} dailyData
	 */
	const generateGraphs = (dailyData) => {
		let datesArr = [];
		let solarON = [];
		let solarOFF = [];
		let solarTotal = [];
		let irradiation = [];
		let PR = [];

		dailyData.forEach((daily) => {
			const date = moment(daily.dateData, "DD-MM-YYYY").format("DD/MM");

			datesArr.push(date);
			solarON.push(daily.solarON);
			solarOFF.push(daily.solarOFF);
			solarTotal.push(daily.solarTotal);
			irradiation.push(daily.irradiation);
			PR.push(formatNumber(daily.PR, 2));
		});

		const optionsProd = {
			animation: false,
			color: ["#f28a10", "#ffc602", "#86bc25"],
			legend: {
				data: [
					`Production ${peakLabels[0]}`,
					`Production ${peakLabels[1]}`,
					"Irradiation",
				],
				bottom: -5,
			},
			textStyle: {
				fontSize: 9,
			},
			height: 200,
			xAxis: [
				{
					axisLabel: {
						fontSize: 10,
						interval: 0,
						rotate: 40,
					},
					type: "category",
					axisTick: {
						show: false,
					},
					data: datesArr,
				},
			],
			yAxis: [
				{
					axisLabel: {
						fontSize: 10,
					},
					axisLine: {
						show: true,
					},
					axisTick: {
						show: true,
					},
					type: "value",
					name: "kWh",
				},
				{
					axisLabel: {
						fontSize: 10,
					},
					axisLine: {
						show: true,
					},
					axisTick: {
						show: true,
					},
					axisTick: {
						show: true,
					},
					type: "value",
					name: "kWh/m\xB2",
				},
			],
			series: [
				{
					name: `Production ${peakLabels[1]}`,
					type: "bar",
					stack: "ProdStack",
					barGap: 0,
					data: solarOFF,
				},
				{
					name: `Production ${peakLabels[0]}`,
					type: "bar",
					stack: "ProdStack",
					barGap: 0,
					data: solarON,
				},
				{
					name: "Irradiation",
					type: "line",
					yAxisIndex: 1,
					data: irradiation,
				},
			],
		};

		const optionsPR = {
			animation: false,
			color: ["#86bc25"],
			legend: {
				data: ["Performance Ratio (PR)"],
				bottom: -5,
			},
			textStyle: {
				fontSize: 10,
			},
			height: 200,
			xAxis: {
				axisLabel: {
					fontSize: 10,
					interval: 0,
					rotate: 40,
				},
				type: "category",
				axisTick: {
					show: false,
				},
				data: datesArr,
			},
			yAxis: {
				max: 100,
				axisLabel: {
					fontSize: 10,
				},
				axisLine: {
					show: true,
				},
				axisTick: {
					show: true,
				},
				type: "value",
				name: "% Performance Ratio",
			},
			series: {
				name: "Performance Ratio (PR)",
				type: "bar",
				data: PR,
				label: {
					show: true,
					position: "top*", // top
					offset: [0, -10],
					interval: 0,
					color: "black",
					rotate: 45,
				},
			},
		};

		setOptionsPR(optionsPR);
		setOptionsProd(optionsProd);
	};

	/**
	 * Generate legends of table
	 * @param {Country of selected site} country
	 * @returns
	 */
	const getOffPeakLegends = (country) => {
		const pubHolidayLegend =
			country === "TH"
				? [
					"",
					{
						text: "Public Holidays",
						style: ["mainTable", "boldHeader", { alignment: "right" }],
					},
					{
						text: "",
						style: ["mainTable", "pubHolidayRow"],
					},
				]
				: ["", "", ""];

		return [
			...pubHolidayLegend,
			{
				text: "Weekends",
				style: ["mainTable", "boldHeader", { alignment: "right" }],
			},
			{
				text: "",
				style: ["mainTable", "weekendRow"],
			},
		];
	};

	/**
	 * Build Grid Injected table
	 * @param {obj} EVNInputs
	 * @param {obj} contractID
	 * @returns Grid injected table
	 */
	const buildGridInjectTable = (EVNInputs, contractID) => {
		const isSCFITSite = contractID.includes("SCFIT");

		if (EVNInputs != null && isSCFITSite) {
			const EVNTotal =
				EVNInputs.EVN_total > 0
					? formatNumber(EVNInputs.EVN_total, 3)
					: EVNInputs.EVN_total;
			const EVNPeak =
				EVNInputs.EVN_on > 0
					? formatNumber(EVNInputs.EVN_on, 3)
					: EVNInputs.EVN_on;
			const EVNStd =
				EVNInputs.EVN_off > 0
					? formatNumber(EVNInputs.EVN_off, 3)
					: EVNInputs.EVN_off;
			const FITPrice = formatNumber(EVNInputs.FITOn, 0);

			return {
				nodeName: "DIV",
				margin: [0, 15, 0, 0],
				table: {
					body: [
						[
							{
								text: "Grid Injection during the past month",
								colSpan: 8,
								style: "tableHeader",
								border: [true, true, true, true],
							},
							"",
							"",
							"",
							"",
							"",
							"",
							"",
						],
						[
							{ text: "Grid injection (kWh): " },
							{ text: EVNTotal, style: "tableHeader" },
							{ text: `${peakLabels[0]} Production to grid (kWh): ` },
							{ text: EVNPeak, style: "tableHeader" },
							{ text: `${peakLabels[1]} Production to grid (kWh): ` },
							{ text: EVNStd, style: "tableHeader" },
							{ text: "Tariff" },
							{ text: FITPrice, style: "tableHeader" },
						],
					],
					widths: ["15%", "10%", "15%", "10%", "15%", "10%", "15%", "10%"],
				},
			};
		} else return {};
	};

	const buildHeader = site => ({
		columns: [
			{
				nodeName: "DIV",
				width: "100%",
				stack: [
					{
						nodeName: "DIV",
						table: {
							body: [
								[
									"",
									{
										text: `${site.label} - Production / Performance Report`,
										border: [true, true, true, true],
										margin: [15, 5, 15, 5],
										alignment: "center",
										style: ["boldHeader", "mainHeader"],
									},
									"",
								],
							],
							widths: ["10%", "80%", "10%"],
						},
						layout: {
							defaultBorder: false,
						},
					},
				],
			},
		],
		margin: [0, 10],
	});
	/**
	 * Generate technical report
	 * @param {Daily data from DB} dailyData
	 * @param {Monthly data from DB} monthlyData
	 * @param {Index on meter(s)} index
	 */
	const generatePDF = (dailyData, monthlyData, graphRef, issues) => {
		getInvoiceName(site.value);
		const solarModules = getSolarPanels(site.value);
		const EVNInputs = site.EVNInputs != null ? site.EVNInputs : null;
		const contractID =
			site.value.contractID != null ? site.value.contractID : "";
		const getStatus = () => {
			let state = ""
			switch (site.value.operation_status) {
				case 2:
					state = 'MSA'
					break;
				case 3:
					state = 'COD'
					break;
				default:
					break;
			}
			return state;
		}
		const operationStatus = getStatus();
		const statusDate = site.value[operationStatus.toLowerCase()];
		const docDefinition = {
			pageSize: "A4",
			content: [
				{
					nodeName: "DIV",
					margin: 10,
					style: ["techReportStyle"],
					stack: [
						{
							nodeName: "DIV",
							width: "100%",
							stack: [
								{
									nodeName: "DIV",
									table: {
										body: [
											[
												"",
												{
													text: `${site.label} - Production / Performance Report`,
													border: [true, true, true, true],
													margin: [15, 5, 15, 5],
													alignment: "center",
													style: ["boldHeader", "mainHeader"],
												},
												"",
											],
										],
										widths: ["10%", "80%", "10%"],
									},
									layout: {
										defaultBorder: false,
									},
								},
							],
						},
						{
							nodeName: "DIV",
							style: ["top_table"],
							table: {
								body: [
									[
										"",
										"",
										{
											text: "Date of report",
											alignment: "right",
										},
										{
											text: dateOfReport,
											alignment: "right",
										},
									],
									[
										{
											text: "General Information",
											colSpan: 4,
											style: "boldHeader",
											border: [true, true, true, true],
										},
									],
									[
										{
											text: "Client",
											border: [true, false, false, false],
										},
										{
											text: site.value.company,
										},
										{
											text: "Plant Capacity",
											border: [false, false, false, false],
										},
										{
											text: `${site.value.power} kWp`,
											border: [false, false, true, false],
										},
									],
									[
										{
											text: "Address",
											border: [true, false, false, false],
										},
										{
											text: site.value.city,
										},
										{
											text: "Number of panels",
											border: [false, false, false, false],
										},
										{
											text: solarModules,
											border: [false, false, true, false],
										},
									],
									[
										{
											text: "Type of plant",
											border: [true, false, false, true],
										},
										{
											text: "Rooftop",
											border: [false, false, false, true],
										},
										{
											text: operationStatus === "COD" ? "COD" : "MSA Date",
											border: [false, false, false, true],
										},
										{
											text: moment(statusDate).format("DD-MMM-YYYY"),
											border: [false, false, true, true],
										},
									],
								],
								widths: ["15%", "45%", "15%", "25%"],
							},
							layout: {
								defaultBorder: false,
							},
						},
						{
							nodeName: "DIV",
							margin: [0, 10, 0, 0],
							table: {
								body: [
									[
										{
											text: `Daily Production - ${fullMonthYear}`,
											style: "tableHeader",
											border: [true, true, true, true],
										},
									],
								],
								widths: ["100%"],
							},
							layout: {
								defaultBorder: false,
							},
						},
						{
							nodeName: "DIV",
							table: {
								body: [
									[
										{
											text: "Total Production (kWh)",
											border: [true, false, true, true],
										},
										{
											text: formatNumber(monthlyData.solar_total, 3),
											style: "tableHeader",
											border: [true, false, true, true],
										},
										{
											text: "",
											border: [true, false, true, false],
										},
										{
											text: `${peakLabels[0]} Production (kWh)`,
											border: [true, false, true, true],
										},
										{
											text: formatNumber(monthlyData.solar_ON, 3),
											style: "tableHeader",
											border: [true, false, true, true],
										},
										{
											text: `${peakLabels[1]} Production (kWh)`,
											border: [true, false, true, true],
										},
										{
											text: formatNumber(monthlyData.solar_OFF, 3),
											style: "tableHeader",
											border: [true, false, true, true],
										},
									],
								],
								widths: ["20%", "10%", "10%", "20%", "10%", "20%", "10%"],
							},
							layout: {
								defaultBorder: false,
							},
						},
						{
							image: graphRef[0],
							width: 570,
						},
						{
							nodeName: "DIV",
							margin: [0, 10, 0, 0],
							table: {
								body: [
									[
										{
											text: `Daily Performance Ratio- ${fullMonthYear}`,
											style: "tableHeader",
											border: [true, true, true, true],
										},
									],
								],
								widths: ["100%"],
							},
							layout: {},
						},
						{
							nodeName: "DIV",
							table: {
								body: [
									[
										{
											text: "Monthly performance Ratio (%)",
											border: [true, false, true, true],
										},
										{
											text: monthlyData.brut_PR,
											style: "tableHeader",
											border: [true, false, true, true],
										},
										{
											text: "",
											border: [true, false, false, false],
										},
									],
								],
								widths: ["20%", "10%", "70%"],
							},
							layout: {
								defaultBorder: false,
							},
						},
						{
							image: graphRef[1],
							width: 570,
						},
						hideUnusedTable ? null : buildUnusedTable(site, monthlyData),
						buildGridInjectTable(EVNInputs, contractID),
						{
							nodeName: "DIV",
							margin: [0, 15, 0, 0],
							table: {
								body: getGRPMeterTable(site, monthlyData, dailyData),
								widths: ["15%", "15%", "15%", "15%", "10%", "10%", "10%", "10%"],
							},
							layout: {
								defaultBorder: false,
							},
						},
						{
							nodeName: "DIV",
							margin: [0, 15, 0, 0],
							table: {
								style: "issuesTable",
								body: getIssuesTable(issues, site.value.operation_status, monthlyData, site.value.cod),
								widths: ["50%", "50%"],
							},
							layout: {
								defaultBorder: false,
							},
						},
					]
				},
				{
					pageBreak: "before",
					text: "",
				},
				{
					nodeName: "DIV",
					margin: 10,
					style: ["techReportStyle"],
					stack: [
						{
							nodeName: "DIV",
							width: "100%",
							stack: [
								{
									nodeName: "DIV",
									table: {
										body: [
											[
												"",
												{
													text: `${site.label} - Production / Performance Report`,
													border: [true, true, true, true],
													margin: [15, 5, 15, 5],
													alignment: "center",
													style: ["boldHeader", "mainHeader"],
												},
												"",
											],
										],
										widths: ["10%", "80%", "10%"],
									},
									layout: {
										defaultBorder: false,
									},
								},
							],
						},
						{
							nodeName: "DIV",
							margin: [0, 10, 0, 0],
							table: {
								body: [
									[
										{
											text: `Daily Data - ${fullMonthYear}`,
											style: "tableHeader",
											border: [true, true, true, false],
										},
									],
								],
								widths: ["100%"],
							},
							layout: {},
						},
						{
							nodeName: "DIV",
							fontSize: 8,
							table: {
								heights: 12,
								body: getTableBody(dailyData, monthlyData, site.holidaysDates),
								widths: ["17%", "17%", "17%", "17%", "17%", "15%"],
							},
							layout: {},
						},
						{
							nodeName: "DIV",
							margin: [0, 3, 0, 0],
							table: {
								body: [getOffPeakLegends(site.value.country)],
								widths: ["73%", "12.5%", "1%", "12.5%", "1%"],
							},
							layout: {
								defaultBorder: false,
							},
						},
						hideUnusedTable && afterYearlyStart(siteValue.date) ? {
							pageBreak: "before",
							nodeName: "DIV",
							margin: [0, 10, 0, 0],
							table: {
								body: [
									[
										{
											text: `Contract Year: `,
											style: "tableHeader",
											border: [true, true, true, true],
										},
										{
											text: `${contractY}`,
											style: "mainTable",
											border: [true, true, true, true],
										}
									],
									[
										{
											text: `Starting Month: `,
											style: "tableHeader",
											border: [true, false, true, true],
										},
										{
											text: `${startingM}`,
											style: "mainTable",
											border: [true, false, true, true],
										}
									]
								],
								widths: ["20%", "19%"],
							},
							layout: {},
						} : null,
						hideUnusedTable && afterYearlyStart(siteValue.date) ? {
							nodeName: "DIV",
							margin: [0, 30, 0, 0],
							fontSize: 8,
							table: {
								heights: 12,
								body: getYearlyTable(yearlyData),
								widths: ["21%", "20%", "21%", "19%", "19%"],
							},
							layout: {},
						} : null,
					]
				},
			],
			pageBreakBefore: (
				currentNode,
				followingNodesOnPage,
				nodesOnNextPage,
				previousNodesOnPage
			) =>
				pageBreakIssuesTable(
					currentNode,
					followingNodesOnPage,
					nodesOnNextPage,
					previousNodesOnPage
				),
			pageMargins: 0,
			styles: {
				addressHeader: { color: "black" },
				boldHeader: { bold: true },
				mainHeader: { fontSize: 10 },
				mainTable: { color: "black" },
				tableHeader: { fillColor: "#a4aaaa", color: "white", bold: true },
				oddCells: { fillColor: "#ebebeb" },
				pubHolidayRow: { fillColor: publicHolidayColour },
				weekendRow: { fillColor: weekendColour },
				techReportStyle: {
					font: "Calibri",
					fontSize: 7,
					color: "black",
				}
			},
			defaultStyle: {
				font: "Calibri",
				fontSize: 7,
				color: "black",
			},
		};

		// notify parent that docDefinition is ready
		onDocDefinition(clone(docDefinition));

		const pdfDocGenerator = pdfMake.createPdf(clone(docDefinition));
		setPDFDoc(clone(docDefinition));

		pdfDocGenerator.getDataUrl((dataUrl) => {
			setPDF(dataUrl);
		});
	};

	/**
	 * Page break if issues table have too many rows
	 * @param {obj} currentNode
	 * @param {obj} followingNodesOnPage
	 * @param {obj} nodesOnNextPage
	 * @param {obj} previousNodesOnPage
	 * @returns boolean
	 */
	const pageBreakIssuesTable = (
		currentNode,
		followingNodesOnPage,
		nodesOnNextPage,
		previousNodesOnPage
	) =>
		currentNode.table != null &&
		currentNode.table.style === "issuesTable" &&
		currentNode.pageNumbers.length > 1;

	/**
	 * Generate graph as image
	 * @param {Index of graphs} no
	 * @param {Set graph state} setFunc
	 */
	const viewCanvas = (no, setFunc) => {
		const canvas = document.getElementsByTagName("canvas")[no];
		setFunc(canvas.toDataURL("image/png"));
	};

	/**
	 * Check if indes is odd or even
	 * @param {number} i
	 * @returns boolean
	 */
	const isOddIndex = (i) => i % 2 !== 0;

	useEffect(() => {
		fetchDailyData(props.selectedSite);
		fetchIssues(props.selectedSite);
		fetchUnused(props.selectedSite);
	}, []);

	useEffect(() => {
		if (dailyData != null && monthlyData != null && index != null)
			generateGraphs(dailyData, monthlyData);
	}, [dailyData, monthlyData, index]);

	useEffect(() => {
		if (graphProdRef != null && graphPRRef != null && issues != null) {
			const graphRef = [graphProdRef, graphPRRef];
			generatePDF(dailyData, monthlyData, graphRef, issues);
		}
	}, [graphProdRef, graphPRRef, issues]);

	return (
		<>
			{optionsProd && (
				<ReactEcharts
					id="echarts"
					option={optionsProd}
					onChartReady={() => viewCanvas(0, setGraphProdRef)}
					style={echartsStyle}
				/>
			)}
			{optionsPR && (
				<ReactEcharts
					id="echarts"
					option={optionsPR}
					onChartReady={() => viewCanvas(1, setGraphPRRef)}
					style={echartsStyle}
				/>
			)}
			{PDF != null ? (
				<>
					<iframe title="Technical Report" style={{ width: "98%", height: "72vh" }} src={PDF} />
					<Row>
						<Button onClick={downloadPDF}>Download</Button>
					</Row>
				</>
			) : (
				<LoaderGraph style={{ height: "72vh" }} />
			)}
		</>
	);
}

export default TechnicalReportTemplate;
